import React from 'react'
import {
  BaseClipspecPostMessagePayload,
  ClipspecClearDataQueryParam,
  ClipspecClearDataQueryParamList,
  ClipspecIconicProductData,
  ClipspecProductPostMessagePayload,
  ClipspecWorkflow,
  ClipspecWorkflowQueryParam,
  IconicBuilderClipspecNamespace,
} from './clipspec-api/ClipspecIntegrationAPI'
import SpinnerPopup from '../components/SpinnerPopup'
import AlertPopup from '../components/AlertPopup'
import { getClipspecProductDataPayload, getImageLoadFailureCount } from './getClipspecProductDataPayload'
import { getAllowedClipspecOrigins, getClipspecMessageTargetOrigin } from './ClipspecMessageOriginUtils'
import { ClipspecConnectionErrorCode } from './ClipspecConnectionErrorCode'
import App from '../App'
import { ClipspecHandoffImageErrorPopup } from './ClipspecHandoffImageErrorPopup'
import { ClipspecConnectionError } from './ClipspecConnectionError'
import { logDebug, logErrorDebug } from '../debug/debugLogging'
import { ClipspecContext } from './ClipspecContext'
import { ClipspecConnectionErrorPopup } from './ClipspecConnectionErrorPopup'

function postClipspecMessage<T extends BaseClipspecPostMessagePayload<string, unknown>>(action: T['action'], data: T['data'], origin: string) {
  window.parent.postMessage({
    action: action,
    data,
  }, origin)
}

type ClipspecConnectionState = {
  state: 'pending'
} | {
  state: 'error'
  code: string
} | {
  state: 'connected'
  origin: string
  workflow: ClipspecWorkflow
}

type ProductHandoffState =
  { state: 'idle' }
  | { state: 'loading' }
  | { state: 'error' }
  | { state: 'warning', payload: ClipspecIconicProductData[] }

export const ClipspecAppWrapper = () => {
  const [connectionState, setConnectionState] = React.useState<Readonly<ClipspecConnectionState>>({ state: 'pending' })

  const clearListRequested = new URLSearchParams(window.location.search).get(ClipspecClearDataQueryParam) === ClipspecClearDataQueryParamList

  const [productHandoffState, setProductHandoffState] = React.useState<ProductHandoffState>({ state: 'idle' })

  React.useEffect(() => {
    try {
      const workflow = new URLSearchParams(window.location.search).get(ClipspecWorkflowQueryParam)

      if (workflow !== ClipspecWorkflow.Consultation && workflow !== ClipspecWorkflow.Product) {
        throw new ClipspecConnectionError('A valid Clipspec workflow was not provided', ClipspecConnectionErrorCode.InvalidWorkflow)
      }

      let allowedOriginList: string[]

      try {
        allowedOriginList = getAllowedClipspecOrigins()
      } catch (e) {
        throw new ClipspecConnectionError(
          e instanceof Error ? e.message : 'error in getAllowedClipspecOrigins',
          ClipspecConnectionErrorCode.InvalidConfiguration,
        )
      }

      let targetOrigin: string

      try {
        targetOrigin = getClipspecMessageTargetOrigin(document.referrer, allowedOriginList)
      } catch (e) {
        throw new ClipspecConnectionError(
          e instanceof Error ? e.message : 'error in getClipspecMessageTargetOrigin',
          ClipspecConnectionErrorCode.InvalidOrigin,
        )
      }

      try {
        postClipspecMessage(
          `${IconicBuilderClipspecNamespace}.ConnectionTest`,
          undefined,
          targetOrigin,
        )
      } catch (e) {
        throw new ClipspecConnectionError(
          e instanceof Error ? e.message : 'error in postClipspecMessage test',
          ClipspecConnectionErrorCode.MessageSendingFailed,
        )
      }

      logDebug('Clipspec', 'connected')
      setConnectionState({
        state: 'connected',
        origin: targetOrigin, workflow: workflow,
      })
    } catch (e) {
      logErrorDebug('Clipspec', 'error connecting', e)
      setConnectionState({
        state: 'error',
        code: e instanceof ClipspecConnectionError ? e.code : ClipspecConnectionErrorCode.Unknown,
      })
    }
  }, [])


  if (connectionState.state === 'error') {
    return (
      <ClipspecConnectionErrorPopup
        errorCode={connectionState.code}
      />
    )
  }

  if (connectionState.state === 'connected') {

    /**
     * Performs the final product data handoff to Clipspec
     * Updates the loading state on completion or error
     * @param payload
     */
    const handoffProductsPayload = (payload: ClipspecIconicProductData[]) => {
      try {
        logDebug('handoff payload', payload)

        postClipspecMessage<ClipspecProductPostMessagePayload>(
          `${IconicBuilderClipspecNamespace}.Handoff`,
          payload,
          connectionState.origin,
        )

        setProductHandoffState({ state: 'idle' })
      } catch (e) {
        setProductHandoffState({ state: 'error' })
      }
    }

    return (
      <ClipspecContext.Provider
        value={{
          workflow: connectionState.workflow,
          clearListRequested,
          onHandoffList: async (list) => {
            if (productHandoffState.state !== 'idle') {
              return
            }

            try {
              setProductHandoffState({ state: 'loading' })
              const payload = await getClipspecProductDataPayload(list)

              if (getImageLoadFailureCount(payload) > 0) {
                // no errors, send immediately
                setProductHandoffState({ state: 'warning', payload })
              } else {
                // image errors, show confirmation
                handoffProductsPayload(payload)
              }
            } catch (e) {
              setProductHandoffState({ state: 'error' })
            }
          }
        }}
      >
        <div
          className="ClipspecAppWrapper"
          style={{
            width: '100vw',
            height: '100vh',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <App/>
        </div>

        {/* unlikely to be seen, most likely if cache cleared and not all images are loaded yet */}
        {productHandoffState?.state === 'loading' && (
          <SpinnerPopup>
            Sending to Clipspec...
          </SpinnerPopup>
        )}

        {productHandoffState.state === 'error' && (
          <AlertPopup
            buttonLabel="Back"
            onClose={() => {
              setProductHandoffState({ state: 'idle' })
            }}
            message="Something went wrong..."
          />
        )}

        {productHandoffState.state === 'warning' && (
          <ClipspecHandoffImageErrorPopup
            numErrors={getImageLoadFailureCount(productHandoffState.payload)}
            workflow={connectionState.workflow}
            onContinue={() => {
              handoffProductsPayload(productHandoffState.payload)
            }}
            onBack={() => {
              setProductHandoffState({ state: 'idle' })
            }}
          />
        )}
      </ClipspecContext.Provider>
    )
  }

  // pending
  return <></>
}